// Thin wrappers over the console's Field + register convention.
//
// Every form here repeats the same five lines: Field with data-invalid, a label,
// the control with aria-invalid, and FieldError. Repeating it by hand is how
// aria-invalid ends up missing on the one field nobody checked. These keep the
// wiring in one place while staying ordinary inputs — no context, no provider, so
// they drop into existing forms unchanged.

import {
	Field,
	FieldDescription,
	FieldError,
	FieldLabel,
} from "@veil/ui/components/field";
import { Input } from "@veil/ui/components/input";
import {
	NativeSelect,
	NativeSelectOption,
} from "@veil/ui/components/native-select";
import type { ComponentProps, ReactNode } from "react";
import type { FieldError as RhfFieldError } from "react-hook-form";

interface BaseProps {
	id: string;
	label: ReactNode;
	/** Shown under the control; omit when the label says enough. */
	description?: ReactNode;
	error?: RhfFieldError;
	className?: string;
}

export function TextField({
	id,
	label,
	description,
	error,
	className,
	...inputProps
}: BaseProps & Omit<ComponentProps<typeof Input>, "id">) {
	return (
		<Field className={className} data-invalid={!!error}>
			<FieldLabel htmlFor={id}>{label}</FieldLabel>
			<Input id={id} aria-invalid={!!error} {...inputProps} />
			{description ? <FieldDescription>{description}</FieldDescription> : null}
			<FieldError errors={[error]} />
		</Field>
	);
}

export interface SelectOption {
	value: string | number;
	label: ReactNode;
}

export function SelectField({
	id,
	label,
	description,
	error,
	className,
	options,
	placeholder,
	...selectProps
}: BaseProps &
	Omit<ComponentProps<typeof NativeSelect>, "id" | "children"> & {
		options: SelectOption[];
		/** Rendered as a disabled first option when there is nothing selected. */
		placeholder?: string;
	}) {
	return (
		<Field className={className} data-invalid={!!error}>
			<FieldLabel htmlFor={id}>{label}</FieldLabel>
			<NativeSelect id={id} aria-invalid={!!error} {...selectProps}>
				{placeholder ? (
					<NativeSelectOption value="" disabled>
						{placeholder}
					</NativeSelectOption>
				) : null}
				{options.map((o) => (
					<NativeSelectOption key={String(o.value)} value={o.value}>
						{o.label}
					</NativeSelectOption>
				))}
			</NativeSelect>
			{description ? <FieldDescription>{description}</FieldDescription> : null}
			<FieldError errors={[error]} />
		</Field>
	);
}
